import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { useTheme } from 'styled-components';
import { Checkbox } from '@statsbomb/kitbag-components';
import { Grid } from '@hudl/uniform-web-grid';
import { useQuery, useReactiveVar } from '@apollo/client';
import { mf_Games } from '../../../apollo';
import { GET_GAME_PLAYS } from '../GamePlayByPlay/GetGamePlays';
import { GET_GAME_DRAWN_PENALTIES } from './GetGameOverview';
import { formatPlay } from '../GamePlayByPlay/PlayByPlay.DataManipulation';
import { resolveDrawnPenaltyPlayer } from './PlaysGrid.dataManipulation';
import { nullsLastComparator } from './PlaysGrid.sorting';
import { OffenseCell, PlayByPlayGridWrap } from './GameOverview.styles';
import TeamIcon from './TeamIcon';

// Cell renderer for the leading selection column. Replaces AG Grid's
// built-in selection / row numbers columns with a kitbag Checkbox. No
// functionality wired up yet — just the visual.
// Reads selection state from AG Grid's row node so toggling a checkbox
// doesn't force a rowData rebuild / full grid re-render. The `rowSelected`
// event fires whenever the node's selection flips.
function SelectCheckboxCellRenderer({ data, node }) {
  const [selected, setSelected] = useState(() => node?.isSelected() ?? false);
  useEffect(() => {
    if (!node) return undefined;
    const onChange = () => setSelected(node.isSelected());
    node.addEventListener('rowSelected', onChange);
    return () => node.removeEventListener('rowSelected', onChange);
  }, [node]);
  if (!data) return null;
  return (
    <Checkbox
      id={`play-select-${data.id}`}
      label="Select play"
      withLabel={false}
      size="small"
      checked={selected}
      onChange={() => node.setSelected(!node.isSelected())}
    />
  );
}

// Selected rows that currently pass the column filters. Plays the user
// selected and then filtered out stay selected in AG Grid (so they come back
// ticked when the filter is cleared), but they are not counted and never sent
// to the video modal while hidden.
export const getVisibleSelectedNodes = (api) => {
  const nodes = [];
  api.forEachNodeAfterFilter((node) => {
    if (node.isSelected()) nodes.push(node);
  });
  return nodes;
};

// Header "select all" checkbox driven by AG Grid's selection API. Listens
// to the grid's selectionChanged and filterChanged events so the
// checked/indeterminate state stays in sync without needing param refreshes.
// Filtering never fires selectionChanged, hence the second listener.
function SelectAllCheckboxHeader({ api }) {
  const [{ allSelected, someSelected }, setCounts] = useState({
    allSelected: false,
    someSelected: false,
  });
  useEffect(() => {
    if (!api) return undefined;
    const update = () => {
      const total = api.getDisplayedRowCount();
      const selectedCount = getVisibleSelectedNodes(api).length;
      setCounts({
        allSelected: total > 0 && selectedCount === total,
        someSelected: selectedCount > 0 && selectedCount < total,
      });
    };
    update();
    api.addEventListener('selectionChanged', update);
    api.addEventListener('filterChanged', update);
    return () => {
      api.removeEventListener('selectionChanged', update);
      api.removeEventListener('filterChanged', update);
    };
  }, [api]);
  return (
    <Checkbox
      id="play-select-all"
      label="Select all plays"
      withLabel={false}
      size="small"
      checked={allSelected}
      indeterminate={someSelected}
      onChange={() =>
        allSelected ? api?.deselectAll('filtered') : api?.selectAll('filtered')
      }
    />
  );
}

// Mirrors the Chromatic "Data Visualization/Grid – Demo" story args so we
// pick up the canonical Grid config without any local alterations. We can
// adjust from here once it's in.
const DEMO_DEFAULT_COL_DEF = {
  sortable: true,
  // Blank cells stay at the bottom whichever way a column is sorted, as the
  // previous plays table did. AG Grid's default puts them first ascending.
  comparator: nullsLastComparator,
  filter: true,
  resizable: true,
  // Plays are read-only — never let a click drop into edit mode.
  editable: false,
  suppressHeaderMenuButton: false,
  suppressHeaderFilterButton: false,
  // Show a dash for any cell whose value is null, undefined or empty string.
  // Column-specific valueFormatters take precedence over this default and are
  // each responsible for returning '-' for their own empty case.
  valueFormatter: ({ value }) => (value == null || value === '' ? '-' : value),
};

// One column per Play field exposed by GET_GAME_PLAYS. Nested objects
// (personnel, penalties, team refs) are flattened via valueGetter so the
// grid receives plain values it can sort / filter on.
const formatBool = (v) => (v == null ? '-' : v ? 'Yes' : 'No');
// Spread into every yes/no column. `cellDataType: false` stops AG Grid from
// turning true/false values into disabled checkboxes, which would ignore the
// formatter. 'Yes', 'No' and '-' for null.
const boolColumn = {
  cellDataType: false,
  valueFormatter: ({ value }) => formatBool(value),
};
// Column-wide 2dp formatter — integers get the trailing zeros (e.g. 10 →
// "10.00") when the column contains any decimal value, keeping vertical
// alignment of decimal points. Null/empty falls through to '-'.
const twoDpForColumn = ({ value }) => {
  if (value == null || value === '') return '-';
  return typeof value === 'number' ? value.toFixed(2) : value;
};
const formatOffPers = (p) =>
  p && typeof p === 'object'
    ? `${p.RB ?? 0}-${p.TE ?? 0}-${p.WR ?? 0}-${p.OL ?? 0}-${p.QB ?? 0}`
    : '-';
const formatDefPers = (p) =>
  p && typeof p === 'object' ? `${p.DL ?? 0}-${p.LB ?? 0}-${p.DB ?? 0}` : '-';
const formatPenalties = (penalties) =>
  Array.isArray(penalties) && penalties.length
    ? penalties.map((pen) => pen.name || pen.type).join('; ')
    : '-';
const formatTeam = (t) => t?.shortName || t?.name || '-';
// Team cell: the team icon from the drive table, then the short name. The
// column's valueGetter still supplies the plain text for sorting.
const teamCell = (key) => ({
  valueGetter: (p) => formatTeam(p.data?.[key]),
  cellRenderer: ({ data, context, value }) => (
    <OffenseCell>
      <TeamIcon team={data?.[key]} context={context} size="tiny" />
      {value}
    </OffenseCell>
  ),
});
// Schema enum values arrive either as the raw key ("TREY") or prefixed
// with the type name ("FormationGroup.TREY"). Strip the prefix and Title
// Case the snake_case payload.
const formatEnum = (raw) => {
  if (!raw || typeof raw !== 'string') return '-';
  return raw
    .replace(/^[A-Z][a-zA-Z]+\./, '')
    .toLowerCase()
    .split('_')
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : ''))
    .join(' ');
};
// MM:SS clock format — quarterClockRemaining is in milliseconds.
const formatClock = (ms) => {
  if (ms == null) return '-';
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
};

const PLAYS_COLUMN_DEFS_BASE = [
  {
    headerName: '',
    colId: 'select',
    headerComponent: SelectAllCheckboxHeader,
    cellRenderer: SelectCheckboxCellRenderer,
    sortable: false,
    filter: false,
    resizable: false,
    editable: false,
    suppressHeaderMenuButton: true,
    suppressHeaderFilterButton: true,
    width: 36,
    minWidth: 36,
    maxWidth: 36,
    pinned: 'left',
    cellClass: 'centered-cell',
    headerClass: 'centered-cell',
  },
  // Fixed width, excluded from autoSize. Autosize measures the header
  // including its menu/filter buttons, so an initialWidth that differs from
  // the measured width makes the pinned column snap on remount. The user can
  // still drag-resize it (resizable comes from the default col def).
  {
    headerName: '#',
    field: 'gameIndex',
    pinned: 'left',
    width: 80,
    minWidth: 56,
    suppressAutoSize: true,
  },
  { headerName: 'DRIVE #', valueGetter: (p) => p.data?.drive?.gameIndex },
  { headerName: 'PLAY #', field: 'driveIndex' },
  { headerName: 'DESCRIPTION', field: 'name' },
  { headerName: 'QTR', field: 'quarter' },
  {
    headerName: 'CLOCK',
    field: 'quarterClockRemaining',
    valueFormatter: ({ value }) => formatClock(value),
  },
  { headerName: 'OFF', ...teamCell('offenseTeam') },
  { headerName: 'DEF', ...teamCell('defenseTeam') },
  { headerName: 'DOWN', field: 'down' },
  // Right-align cells only — keep headers left-aligned. Using cellClass
  // directly avoids `type: 'rightAligned'` which would also apply the
  // header alignment class.
  {
    headerName: 'TO GO',
    field: 'yardsToGo',
    cellClass: 'ag-right-aligned-cell',
  },
  {
    headerName: 'YARD LN',
    field: 'yardLine',
    cellClass: 'ag-right-aligned-cell',
  },
  { headerName: 'TYPE', field: 'type' },
  {
    headerName: 'NET YDS',
    field: 'yardsNet',
    cellClass: 'ag-right-aligned-cell',
  },
  {
    headerName: 'RUN YDS',
    field: 'yardsRun',
    cellClass: 'ag-right-aligned-cell',
  },
  {
    headerName: 'PASS AIR YDS',
    field: 'passYardsAir',
    cellClass: 'ag-right-aligned-cell',
  },
  {
    headerName: 'EPA',
    field: 'scrimmageEpa',
    cellClass: 'ag-right-aligned-cell',
  },
  {
    headerName: 'EXP?',
    field: 'explosive',
    ...boolColumn,
  },
  {
    headerName: 'SUCC',
    field: 'success',
    ...boolColumn,
  },
  {
    headerName: '1ST D WON?',
    field: 'firstDownWon',
    ...boolColumn,
  },
  {
    headerName: 'TD WON?',
    field: 'touchdownWon',
    ...boolColumn,
  },
  {
    headerName: 'PTS WON',
    field: 'pointsWon',
    cellClass: 'ag-right-aligned-cell',
    // Zero points reads as "-", like null: only scoring plays show a number.
    valueFormatter: ({ value }) => value || '-',
  },
  { headerName: 'TO TYPE', field: 'turnoverType' },
  {
    headerName: 'DOWN NEG',
    field: 'downNegated',
    ...boolColumn,
  },
  {
    headerName: 'QB HIT?',
    field: 'qbHit',
    ...boolColumn,
  },
  {
    headerName: 'QB SCK?',
    field: 'qbSacked',
    ...boolColumn,
  },
  {
    headerName: 'OFF PERS',
    valueGetter: (p) => formatOffPers(p.data?.offensivePersonnel),
  },
  {
    headerName: 'RBS',
    field: 'backfieldNumRbs',
    cellClass: 'ag-right-aligned-cell',
  },
  {
    headerName: 'OFF FORM',
    field: 'formationGroup',
    valueFormatter: ({ value }) => formatEnum(value),
  },
  {
    headerName: 'DEF PERS',
    valueGetter: (p) => formatDefPers(p.data?.defensivePersonnel),
  },
  {
    headerName: 'DEF COV',
    field: 'defensiveCoverage',
    valueFormatter: ({ value }) => formatEnum(value),
  },
  {
    headerName: 'MOFO',
    field: 'mofo',
    ...boolColumn,
  },
  {
    headerName: 'OFF PEN ACC?',
    field: 'offensePenaltyAccepted',
    ...boolColumn,
  },
  {
    headerName: 'DEF PEN ACC?',
    field: 'defensePenaltyAccepted',
    ...boolColumn,
  },
  {
    headerName: 'PEN OFF?',
    field: 'penaltyOffset',
    ...boolColumn,
  },
  {
    headerName: 'PEN YDS',
    field: 'penaltyYards',
    cellClass: 'ag-right-aligned-cell',
    // Zero reads as "-". A column formatter skips the shared two-decimal
    // pass, so non-zero values go through twoDpForColumn here.
    valueFormatter: (params) => (params.value ? twoDpForColumn(params) : '-'),
  },
  {
    headerName: 'PEN 1D?',
    field: 'penaltyFirstDown',
    ...boolColumn,
  },
  {
    headerName: 'PEN D LOSS?',
    field: 'penaltyDownLoss',
    ...boolColumn,
  },
  {
    headerName: 'PENS',
    valueGetter: (p) => formatPenalties(p.data?.penalties),
  },
  {
    headerName: 'DRAWN PENALTY',
    field: 'drawnPenaltyPlayer',
  },
  { headerName: 'HOME SCORE', field: 'homeScore' },
  { headerName: 'AWAY SCORE', field: 'awayScore' },
];

// Inspect the row data to find which fields have at least one non-integer
// number; those columns get the column-wide twoDpForColumn formatter
// applied unless they already define their own (booleans, enums, etc.).
const applyTwoDpToDecimalColumns = (rowData) => {
  const decimalFields = new Set();
  if (Array.isArray(rowData)) {
    rowData.forEach((row) => {
      PLAYS_COLUMN_DEFS_BASE.forEach((def) => {
        if (!def.field) return;
        const v = row?.[def.field];
        if (typeof v === 'number' && !Number.isInteger(v)) {
          decimalFields.add(def.field);
        }
      });
    });
  }
  return PLAYS_COLUMN_DEFS_BASE.map((def) => {
    if (def.valueFormatter || def.cellRenderer) return def;
    if (def.field && decimalFields.has(def.field)) {
      return { ...def, valueFormatter: twoDpForColumn };
    }
    return def;
  });
};

// T2 fields: post-snap analytics that are absent in T1-only games.
// When t1DataOnly is true these are zeroed out so the grid reads as
// it would if the real game data were T1-only (matches the drives table).
const T2_FIELDS = [
  'scrimmageEpa',
  'yardsNet',
  'yardsRun',
  'passYardsAir',
  'explosive',
  'success',
  'firstDownWon',
  'touchdownWon',
  'pointsWon',
  'turnoverType',
  'name', // play description — only available post-snap
];

const getPlayRowId = ({ data }) => String(data.id);

function PlaysGrid({
  plays: playsProp = null,
  onSelectionChange = () => {},
  t1DataOnly = false,
  teamContext = null,
  domLayout = undefined,
}) {
  const theme = useTheme();
  const gridApiRef = useRef(null);
  // Columns paint at AG Grid's default width until the first autoSize pass
  // runs, which reads as a snap on remount. Keep the grid hidden (but laid
  // out, so autoSize can measure) until that pass has finished.
  const [isSized, setIsSized] = useState(false);
  const selectedGameId = useReactiveVar(mf_Games);
  // Skip queries when plays are provided directly (e.g. Storybook / unit tests).
  const { data: playsData, loading } = useQuery(GET_GAME_PLAYS, {
    skip: !selectedGameId || !!playsProp,
  });
  const { data: drawnPenaltyData } = useQuery(GET_GAME_DRAWN_PENALTIES, {
    skip: !selectedGameId || !!playsProp,
  });

  // playId -> "Name1, Name2" for players who drew a penalty on that play.
  // Apollo fires GET_GAME_DRAWN_PENALTIES from the cache when GameOverview has
  // already run it; the grid updates automatically when the query resolves.
  const drawnPenaltyByPlayId = useMemo(() => {
    const edges = drawnPenaltyData?.playerPlays?.edges ?? [];
    const map = new Map();
    edges.forEach(({ node }) => {
      const playId = node.play?.id;
      if (!playId) return;
      const playerName = node.player?.knownName || node.player?.name;
      if (!playerName) return;
      const existing = map.get(playId);
      map.set(playId, existing ? `${existing}, ${playerName}` : playerName);
    });
    return map;
  }, [drawnPenaltyData]);

  const rowData = useMemo(() => {
    // When plays are provided as a prop (Storybook / tests), use them directly —
    // they are already formatted and include drawnPenaltyPlayer.
    const plays = playsProp ?? (playsData?.plays?.edges
      ? playsData.plays.edges.map(formatPlay).map((play) => ({
          ...play,
          drawnPenaltyPlayer: resolveDrawnPenaltyPlayer(
            play,
            drawnPenaltyByPlayId.get(play.id),
          ),
        }))
      : null);

    if (!plays) return [];
    if (!t1DataOnly) return plays;
    return plays.map((play) => {
      const stripped = { ...play };
      T2_FIELDS.forEach((f) => {
        stripped[f] = null;
      });
      return stripped;
    });
  }, [playsProp, playsData, t1DataOnly, drawnPenaltyByPlayId]);

  // AG Grid does not redraw cells when `context` changes. The team colours
  // arrive after the first rows render, so refresh the cells once they do or
  // the team icons keep their first (unset, white) colour.
  useEffect(() => {
    gridApiRef.current?.refreshCells({ force: true });
  }, [teamContext]);

  // AG Grid owns the selection state via `rowSelection` below. The
  // selectionChanged event handler bubbles the currently-selected rows
  // up so PlayByPlayPanel can drive the action bar.
  const handleSelectionChanged = useCallback(
    (event) => {
      onSelectionChange?.(
        getVisibleSelectedNodes(event.api).map((node) => node.data),
      );
    },
    [onSelectionChange],
  );

  const columnDefs = useMemo(
    () => applyTwoDpToDecimalColumns(rowData),
    [rowData],
  );

  // Override Uniform's theme with kitbag tokens directly, mirroring the
  // drive table's surface / ink / border / spacing values so the two
  // grids read as part of the same UI. Uniform's theme is built on
  // themeAlpine.withParams(...) — passing themeParams to <Grid> layers
  // our values on top via a second .withParams() call, replacing every
  // var(--u-*) reference Uniform's defaults emit.
  const themeParams = useMemo(
    () => ({
      // Surface + ink.
      backgroundColor: theme.colours.canvas.tertiary.main,
      // Alpine's default oddRowBackgroundColor is slightly lighter than
      // backgroundColor, making every even-indexed row (0, 2, 4 …) appear
      // a different shade. Set it to the same value to disable row striping.
      oddRowBackgroundColor: theme.colours.canvas.tertiary.main,
      foregroundColor: theme.colours.ink.primary.main,
      headerBackgroundColor: theme.colours.canvas.primary.main,
      // The theme's default hover is a light grey that hides the white
      // header text. Use the strong variant of the header colour.
      headerCellHoverBackgroundColor: theme.colours.canvas.primary.strong,
      headerTextColor: theme.colours.ink.primary.strong,
      // Column menu and filter popup: same surface as the app's other
      // flyouts (canvas.tertiary.main), with the table's divider colour for
      // the outline and the lines between menu sections.
      menuBackgroundColor: theme.colours.canvas.tertiary.main,
      menuBorder: {
        width: 1,
        style: 'solid',
        color: theme.colours.canvas.secondary.strong,
      },
      menuSeparatorColor: theme.colours.canvas.secondary.strong,
      // Same drop shadow kitbag uses for its flyouts and menus.
      popupShadow: theme.effects.dropShadow.regular,
      panelBackgroundColor: theme.colours.canvas.secondary.main,
      // Uniform's dataTable theme sets selectedRowBackgroundColor: 'none'
      // and a hover that resolves to an undefined Uniform CSS var. Use the
      // same pair as the drive table (AgGridThemedWrap) so hover and
      // selection look the same in both views.
      rowHoverColor: theme.colours.canvas.secondary.strong,
      selectedRowBackgroundColor: theme.applyOpacity(
        theme.colours.interface.main,
        'highlight',
      ),

      // Typography — cells inherit kitbag bodyMedium, headers inherit
      // headingSmall weight. Font family inherits so the rest of the
      // app's font stack flows through.
      fontFamily: 'inherit',
      fontSize: theme.typography.bodyMedium.fontSize,
      dataFontSize: theme.typography.bodyMedium.fontSize,
      headerFontSize: theme.typography.bodyMedium.fontSize,
      headerFontWeight: theme.typography.headingSmall.fontWeight,

      // Spacing — Alpine's "spacing" param drives row height + vertical
      // padding scale. Matching the drive table's --ag-cell-horizontal-padding
      // here keeps row/header heights and inset equal between grids.
      spacing: theme.sizing.small,
      cellHorizontalPadding: theme.sizing.small,

      // Borders — 1px solid kitbag canvas.secondary.strong, same as the
      // drive table's row dividers, both horizontally and vertically.
      headerRowBorder: {
        width: 1,
        style: 'solid',
        color: theme.colours.interface.main,
      },
      rowBorder: {
        width: 1,
        style: 'solid',
        color: theme.colours.canvas.secondary.strong,
      },
      columnBorder: {
        width: 1,
        style: 'solid',
        color: theme.colours.canvas.secondary.strong,
      },
      headerColumnBorder: {
        width: 1,
        style: 'solid',
        color: theme.colours.canvas.secondary.strong,
      },
      // No extra border on the last pinned-left column (#): the divider
      // between sticky and scrolling columns is drawn once, on the pinned
      // area's outer edge, by PlayByPlayGridWrap.
      pinnedColumnBorder: false,
      borderRadius: 0,
      wrapperBorderRadius: 0,
    }),
    [theme],
  );

  return (
    <PlayByPlayGridWrap
      data-testid="plays-grid"
      style={
        !isSized && rowData.length > 0 ? { visibility: 'hidden' } : undefined
      }
    >
      <Grid
        rowData={rowData}
        columnDefs={columnDefs}
        context={teamContext}
        onGridReady={(p) => {
          gridApiRef.current = p.api;
        }}
        loading={playsProp ? false : loading}
        themeParams={themeParams}
        // domLayout is undefined in production (AG Grid default: 'normal').
        // Stories pass 'autoHeight' so the grid sizes itself to its row count
        // instead of filling a fixed-height container.
        {...(domLayout !== undefined && { domLayout })}
        // ---- Demo story args, copied verbatim from the Chromatic story ----
        pagination={false}
        paginationPageSize={20}
        suppressMovableColumns
        // AG Grid manages selection state natively. Hide the built-in
        // checkbox UI — our kitbag Checkbox renderer drives `node.setSelected`
        // and the header drives `api.selectAll` / `api.deselectAll`. Cells
        // re-render via the node's rowSelected event, not a rowData rebuild.
        rowSelection={{
          mode: 'multiRow',
          checkboxes: false,
          headerCheckbox: false,
          enableClickSelection: false,
          // Header select-all only selects rows that pass the active filter,
          // so "N plays selected" never counts plays the user cannot see.
          selectAll: 'filtered',
        }}
        // Stable row ids let AG Grid keep the user's selection (and scroll
        // position) when rowData is replaced, e.g. when the drawn-penalty
        // query resolves after the plays.
        getRowId={getPlayRowId}
        onSelectionChanged={handleSelectionChanged}
        // Filtering can hide selected rows without firing selectionChanged,
        // so re-report the visible selection whenever the filter changes.
        onFilterChanged={handleSelectionChanged}
        // Render off-screen columns in the DOM upfront so autoSize can
        // measure every column on first paint (not just the visible ones).
        suppressColumnVirtualisation
        // Cells are read-only display — no editing, no click-focus border.
        cellSelection={false}
        suppressCellFocus
        // Force an unconditional fit-to-contents pass once data has
        // rendered, including header width. Uniform's default
        // autoSizeStrategy can under-size valueGetter-based columns and
        // imposes a max-width by default; this call ignores both.
        onFirstDataRendered={(p) => {
          p.api.autoSizeAllColumns(false /* skipHeader */);
          setIsSized(true);
        }}
        // Initial autoSize runs with AG Grid's `ag-column-moving` class
        // applied, which triggers a 0.2s transition on column position —
        // the jarring slide-in seen on first paint.
        suppressColumnMoveAnimation
        defaultColDef={DEMO_DEFAULT_COL_DEF}
        suppressRowVirtualisation={false}
        suppressAutoSize={false}
      />
    </PlayByPlayGridWrap>
  );
}

PlaysGrid.propTypes = {
  /** Pre-formatted plays for Storybook / tests. When provided the component
   *  skips its own Apollo queries and renders directly from this data. */
  plays: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string })),
  onSelectionChange: PropTypes.func,
  t1DataOnly: PropTypes.bool,
  teamContext: PropTypes.shape({
    selectedTeamId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    selectedTeamColor: PropTypes.string,
    opponentTeamColor: PropTypes.string,
  }),
  /** AG Grid domLayout override. Leave undefined in production (uses the AG
   *  Grid default 'normal'). Stories pass 'autoHeight' so the grid sizes to
   *  its row count and Chromatic captures the correct snapshot height. */
  domLayout: PropTypes.string,
};

export default PlaysGrid;
